import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import './EnterpriseModal.css';

const PERSONAL_DOMAINS = [
    'gmail.com', 'yahoo.com', 'yahoo.co.uk', 'hotmail.com', 'hotmail.co.uk',
    'outlook.com', 'live.com', 'icloud.com', 'me.com', 'mac.com',
    'aol.com', 'protonmail.com', 'proton.me', 'yandex.com', 'mail.com',
    'zoho.com', 'gmx.com', 'inbox.com', 'fastmail.com'
];

const isPersonalEmail = (email) => {
    if (!email) return false;
    const domain = email.split('@')[1]?.toLowerCase();
    return !domain || PERSONAL_DOMAINS.includes(domain);
};

const INITIAL_FORM = {
    // Step 1 — required
    workEmail: '',
    companyName: '',
    companySize: '',
    expectedUsers: '',
    expectedMinutes: '',
    useCase: '',
    // Step 2 — optional
    requiresSso: '',
    complianceRequirements: [],
    timeline: ''
};

const EnterpriseModal = ({ isOpen, onClose, onSubmit }) => {
    const { user } = useAuth();
    const { currentWorkspace } = useWorkspace();

    const getInitialForm = () => ({
        ...INITIAL_FORM,
        workEmail: user?.email || '',
        companyName: currentWorkspace?.name || ''
    });

    const [step, setStep] = useState(1);
    const [form, setForm] = useState(getInitialForm());
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    // Update form if user or workspace data loads after initial render
    useEffect(() => {
        if (isOpen && (!form.workEmail || !form.companyName)) {
            setForm(f => ({
                ...f,
                workEmail: f.workEmail || user?.email || '',
                companyName: f.companyName || currentWorkspace?.name || ''
            }));
        }
    }, [isOpen, user, currentWorkspace]);

    if (!isOpen) return null;

    const handleClose = () => {
        setStep(1);
        setForm(getInitialForm());
        setErrors({});
        setSuccess(false);
        onClose();
    };

    const set = (field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        setErrors(e => ({ ...e, [field]: undefined }));
    };

    const toggleCompliance = (value) => {
        setForm(f => ({
            ...f,
            complianceRequirements: f.complianceRequirements.includes(value)
                ? f.complianceRequirements.filter(v => v !== value)
                : [...f.complianceRequirements, value]
        }));
    };

    const validateStep1 = () => {
        const errs = {};
        if (!form.workEmail) errs.workEmail = 'Work email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.workEmail)) errs.workEmail = 'Enter a valid email address';
        else if (isPersonalEmail(form.workEmail)) errs.workEmail = 'Please use a work email address (no Gmail, Yahoo, etc.)';
        if (!form.companyName) errs.companyName = 'Company name is required';
        if (!form.companySize) errs.companySize = 'Please select your company size';
        if (!form.expectedUsers) errs.expectedUsers = 'Please select expected number of users';
        if (!form.expectedMinutes) errs.expectedMinutes = 'Please select expected execution minutes';
        if (!form.useCase.trim()) errs.useCase = 'Please describe your primary use case';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleNext = () => {
        if (validateStep1()) setStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await onSubmit(form);
            setSuccess(true);
        } catch (err) {
            setErrors({ submit: err.message || 'Something went wrong. Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="em-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <div className="em-modal">
                <button className="em-close" onClick={handleClose} aria-label="Close">✕</button>

                {success ? (
                    <div className="em-success">
                        <div className="em-success-icon">🎉</div>
                        <h2>We'll be in touch!</h2>
                        <p>Thanks for your interest in Rova Enterprise. Our sales team will reach out to you at <strong>{form.workEmail}</strong> shortly.</p>
                        <button className="em-btn-primary" onClick={handleClose}>Close</button>
                    </div>
                ) : (
                    <>
                        <div className="em-header">
                            <div className="em-badge">Enterprise Plan</div>
                            <h2>Talk to Sales</h2>
                            <p>Tell us about your team and we'll get back to you with a custom quote.</p>
                            <div className="em-steps">
                                <div className={`em-step ${step >= 1 ? 'active' : ''}`}>
                                    <span>1</span> Team Details
                                </div>
                                <div className="em-step-divider" />
                                <div className={`em-step ${step >= 2 ? 'active' : ''}`}>
                                    <span>2</span> Requirements
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} noValidate>
                            {step === 1 && (
                                <div className="em-fields">
                                    <div className="em-field">
                                        <label>Work Email <span className="em-req">*</span></label>
                                        <input
                                            id="em-work-email"
                                            type="email"
                                            placeholder="you@yourcompany.com"
                                            value={form.workEmail}
                                            onChange={e => set('workEmail', e.target.value)}
                                            className={errors.workEmail ? 'error' : ''}
                                        />
                                        {errors.workEmail && <span className="em-error">{errors.workEmail}</span>}
                                    </div>

                                    <div className="em-field">
                                        <label>Company Name <span className="em-req">*</span></label>
                                        <input
                                            id="em-company-name"
                                            type="text"
                                            placeholder="Acme Corp"
                                            value={form.companyName}
                                            onChange={e => set('companyName', e.target.value)}
                                            className={errors.companyName ? 'error' : ''}
                                        />
                                        {errors.companyName && <span className="em-error">{errors.companyName}</span>}
                                    </div>

                                    <div className="em-row">
                                        <div className="em-field">
                                            <label>Company Size <span className="em-req">*</span></label>
                                            <select
                                                id="em-company-size"
                                                value={form.companySize}
                                                onChange={e => set('companySize', e.target.value)}
                                                className={errors.companySize ? 'error' : ''}
                                            >
                                                <option value="">Select size</option>
                                                <option value="1-10">1–10 employees</option>
                                                <option value="11-50">11–50 employees</option>
                                                <option value="51-200">51–200 employees</option>
                                                <option value="201-1000">201–1,000 employees</option>
                                                <option value="1000+">1,000+ employees</option>
                                            </select>
                                            {errors.companySize && <span className="em-error">{errors.companySize}</span>}
                                        </div>

                                        <div className="em-field">
                                            <label>Expected Users <span className="em-req">*</span></label>
                                            <select
                                                id="em-expected-users"
                                                value={form.expectedUsers}
                                                onChange={e => set('expectedUsers', e.target.value)}
                                                className={errors.expectedUsers ? 'error' : ''}
                                            >
                                                <option value="">Select range</option>
                                                <option value="10-25">10–25 users</option>
                                                <option value="25-100">25–100 users</option>
                                                <option value="100+">100+ users</option>
                                            </select>
                                            {errors.expectedUsers && <span className="em-error">{errors.expectedUsers}</span>}
                                        </div>
                                    </div>

                                    <div className="em-field">
                                        <label>Expected Execution Minutes / Month <span className="em-req">*</span></label>
                                        <select
                                            id="em-expected-minutes"
                                            value={form.expectedMinutes}
                                            onChange={e => set('expectedMinutes', e.target.value)}
                                            className={errors.expectedMinutes ? 'error' : ''}
                                        >
                                            <option value="">Select range</option>
                                            <option value="<5000">Less than 5,000 mins</option>
                                            <option value="5000-20000">5,000–20,000 mins</option>
                                            <option value="20000+">20,000+ mins</option>
                                        </select>
                                        {errors.expectedMinutes && <span className="em-error">{errors.expectedMinutes}</span>}
                                    </div>

                                    <div className="em-field">
                                        <label>What will you primarily use Rova for? <span className="em-req">*</span></label>
                                        <textarea
                                            id="em-use-case"
                                            rows={3}
                                            placeholder="e.g. Regression testing for our mobile banking app across iOS and Android..."
                                            value={form.useCase}
                                            onChange={e => set('useCase', e.target.value)}
                                            className={errors.useCase ? 'error' : ''}
                                        />
                                        {errors.useCase && <span className="em-error">{errors.useCase}</span>}
                                    </div>

                                    <button type="button" className="em-btn-primary" onClick={handleNext}>
                                        Continue →
                                    </button>
                                </div>
                            )}

                            {step === 2 && (
                                <div className="em-fields">
                                    <div className="em-field">
                                        <label>Do you require SSO? <span className="em-optional">Optional</span></label>
                                        <div className="em-radio-group">
                                            {[['yes', 'Yes'], ['no', 'No'], ['not_sure', 'Not sure']].map(([val, label]) => (
                                                <label key={val} className={`em-radio ${form.requiresSso === val ? 'selected' : ''}`}>
                                                    <input type="radio" name="requiresSso" value={val} checked={form.requiresSso === val} onChange={() => set('requiresSso', val)} />
                                                    {label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="em-field">
                                        <label>Compliance Requirements <span className="em-optional">Optional</span></label>
                                        <div className="em-checkbox-group">
                                            {['SOC 2', 'HIPAA', 'GDPR', 'None', 'Not sure'].map(item => (
                                                <label key={item} className={`em-checkbox ${form.complianceRequirements.includes(item) ? 'selected' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={form.complianceRequirements.includes(item)}
                                                        onChange={() => toggleCompliance(item)}
                                                    />
                                                    {item}
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="em-field">
                                        <label>When are you looking to get started? <span className="em-optional">Optional</span></label>
                                        <select id="em-timeline" value={form.timeline} onChange={e => set('timeline', e.target.value)}>
                                            <option value="">Select timeline</option>
                                            <option value="immediately">Immediately</option>
                                            <option value="within_1_month">Within 1 month</option>
                                            <option value="1_3_months">1–3 months</option>
                                            <option value="exploring">Just exploring</option>
                                        </select>
                                    </div>

                                    {errors.submit && (
                                        <div className="em-submit-error">{errors.submit}</div>
                                    )}

                                    <div className="em-actions">
                                        <button type="button" className="em-btn-ghost" onClick={() => setStep(1)}>
                                            ← Back
                                        </button>
                                        <button type="submit" className="em-btn-primary" disabled={submitting}>
                                            {submitting ? 'Submitting...' : 'Request a Demo'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default EnterpriseModal;
